import React, { useState, useEffect, useRef } from 'react';
import { Amplify } from 'aws-amplify';
import { amplifyConfig } from './config/aws-config';
import CustomAuthenticator from './components/CustomAuthenticator';
import { ErrorDisplay } from './components/ErrorDisplay';
import HeroSection from './components/HeroSection';
import Header from './components/Header';
import Footer from './components/Footer';
import { webSocketService, WebSocketMessage, IndustryFormConfig } from './services/websocket';
import { apiService, ResumeInterviewResponse, StateHierarchyData } from './services/api';
import { streamingStateTracker } from './utils/streamingStateTracker';
import ResumeDraft from './components/ResumeDraft';
import ResumeInterview from './components/ResumeInterview';
import MobileTabView from './components/MobileTabView';
import SecureForm from './components/SecureForm';
import IndustrySelectionForm from './components/IndustrySelectionForm';
import UploadModal from './components/UploadModal';
import ProjectsSidebar from './components/ProjectsSidebar';
import { NotificationsSidebar } from './components/NotificationsSidebar';
import type { UploadStage } from './components/DocumentUploadForm';
import { v4 as uuidv4 } from 'uuid';
import BowtieIcon from './components/BowtieIcon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import styles from './App.module.css';
import interviewStyles from './components/ResumeInterview.module.css';
import { useBowtieController } from './hooks/useBowtieController';

// Configure Amplify
Amplify.configure(amplifyConfig);

// Theme management
type Theme = 'light' | 'dark' | 'auto';

const getInitialTheme = (): Theme => {
  const savedTheme = localStorage.getItem('theme') as Theme;
  if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
    return savedTheme;
  }
  return 'auto';
};

const getSystemTheme = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const applyTheme = (theme: Theme) => {
  const root = document.documentElement;
  
  if (theme === 'auto') {
    // Auto mode: use system preference
    const systemTheme = getSystemTheme();
    root.setAttribute('data-theme', systemTheme);
  } else {
    root.setAttribute('data-theme', theme);
  }
};

// Interview types
interface SecureFormField {
  field_id: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  placeholder?: string | null;
  required?: boolean;
  validation_pattern?: string | null;
}

interface SecureFormConfig {
  formId?: string;
  form_id?: string; // Alternative field name
  title: string;
  description: string;
  fields: SecureFormField[];
}

interface DocumentUploadFormConfig {
  resumeInterviewId: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  timestamp?: Date;
  interface?: InterfaceConfig;
  userResponseForAnimation?: UserResponse;
  secureForm?: SecureFormConfig;
  industryForm?: IndustryFormConfig;
  documentUploadForm?: DocumentUploadFormConfig;
  toolName?: string;
  toolCallId?: string;
  isStreaming?: boolean;
}

interface InterfaceConfig {
  type: 'text' | 'buttons' | 'dropdown';
  options?: string[];
  placeholder?: string;
}

interface UserResponse {
  type: 'text' | 'button' | 'dropdown';
  text?: string;
  label?: string;
}

function App() {
  // Theme state
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authDefaultTab, setAuthDefaultTab] = useState<'signIn' | 'signUp'>('signUp');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Interview state - all in one place!
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentInterface, setCurrentInterface] = useState<InterfaceConfig>({ type: 'text', placeholder: "Type your response..." });
  
  // Resume draft state
  const [resumeDocument, setResumeDocument] = useState<string>('');
  const [isDraftPanelOpen, setIsDraftPanelOpen] = useState(true);
  const [draftPanelWidth, setDraftPanelWidth] = useState(400);
  const [isDraftPanelOpenWide, setIsDraftPanelOpenWide] = useState(false);
  const [draftPanelPreviousWidth, setDraftPanelPreviousWidth] = useState(400);
  
  // State hierarchy state
  const [stateHierarchy, setStateHierarchy] = useState<StateHierarchyData | null>(null);
  
  // Todos state
  const [todos, setTodos] = useState<any[]>([]);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [mobileActiveTab, setMobileActiveTab] = useState<'chat' | 'document'>('chat');
  
  // Animation state
  const [pendingApiCallDetails, setPendingApiCallDetails] = useState<{ response: UserResponse, messageId: string, newStateData?: any } | null>(null);
  
  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadStage, setUploadStage] = useState<UploadStage | null>(null);
  
  // Project and version state
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  
  // Sidebar state
  const [isSidebarHoverOpen, setIsSidebarHoverOpen] = useState(false);
  const [isSidebarToggleOpen, setIsSidebarToggleOpen] = useState(false);
  
  // Notifications state
  const [newInsightsCount, setNewInsightsCount] = useState(0);
  const [isNotificationsSidebarOpen, setIsNotificationsSidebarOpen] = useState(false);
  
  // Refs - these persist across renders
  const didInitWebSocketRef = useRef(false);
  const apiCallInProgressRef = useRef(false);
  const initialInterviewStartedRef = useRef(false);
  const currentStreamingMessageIdRef = useRef<string | null>(null);
  const hasReceivedFirstTokenRef = useRef(false);
  const pendingMessageIdRef = useRef<string | null>(null);
  
  // DOM refs
  const inputRef = useRef<HTMLInputElement>(null);
  const chatAreaRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const autoScrollSpacerRef = useRef<HTMLDivElement | null>(null);
  const autoscrollDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Track if autoscroll is already running to prevent multiple simultaneous calls
  const isAutoscrollingRef = useRef(false);
  
  // Initialize bowtie controller
  const bowtie = useBowtieController({ chatAreaRef });
  
  // Track initial flow state explicitly
  const [isInitialFlow, setIsInitialFlow] = useState(false);
  
  
  // Autoscroll to the latest message - simplified and more robust
  const autoscroll = (behavior: ScrollBehavior = 'smooth') => {
    // Prevent multiple simultaneous autoscroll calls
    if (isAutoscrollingRef.current) {
      return;
    }
    
    isAutoscrollingRef.current = true;
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      try {
        const chatSection = document.querySelector(`.${interviewStyles.chatSection}`) as HTMLElement;
        
        if (!chatSection) {
          isAutoscrollingRef.current = false;
          return;
        }

        // Calculate the target scroll position
        // We want the bottom of the viewport to be 1/3 up from the total scrollable height
        const viewportHeight = chatSection.clientHeight;
        const contentHeight = chatSection.scrollHeight;
        const oneThirdFromBottom = viewportHeight / 3;
        
        // Target scroll position: show content with 1/3 of viewport remaining below
        const targetScrollTop = contentHeight - viewportHeight + oneThirdFromBottom;
        
        // Ensure we don't scroll past the available content
        const finalScrollTop = Math.max(0, Math.min(targetScrollTop, contentHeight - viewportHeight));
        
        // Only scroll if we're not already at the target position (with some tolerance)
        const currentScrollTop = chatSection.scrollTop;
        const tolerance = 50; // pixels
        
        if (Math.abs(currentScrollTop - finalScrollTop) > tolerance) {
          chatSection.scrollTo({
            top: finalScrollTop,
            behavior: behavior
          });
        }
      } finally {
        // Always clear the flag after a short delay to allow the scroll to complete
        setTimeout(() => {
          isAutoscrollingRef.current = false;
        }, 100);
      }
    });
  };

  // Apply theme on mount and when theme changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auto theme: listen for system theme changes when in auto mode
  useEffect(() => {
    if (theme !== 'auto') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (theme === 'auto') {
        applyTheme('auto');
      }
    };

    // Listen for system theme changes
    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // WebSocket connection - only when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (!didInitWebSocketRef.current) {
      didInitWebSocketRef.current = true;
      
      const connectWebSocket = async () => {
        try {
          await webSocketService.connect();
        } catch (error) {
          console.error('[App] WebSocket connection error:', error);
          setError('Failed to connect to real-time service');
        }
      };
      connectWebSocket();
    }

    return () => {
      // Only disconnect if we're logging out
      if (!isAuthenticated && didInitWebSocketRef.current) {
        webSocketService.disconnect();
        didInitWebSocketRef.current = false;
      }
    };
  }, [isAuthenticated]);

  // Track current client request ID
  const currentClientRequestIdRef = useRef<string | null>(null);

  // Process API response
  const processApiResponse = React.useCallback((data: ResumeInterviewResponse) => {
    
    if (data.interface && data.interface.type) {
      setCurrentInterface(data.interface);
    } else {
      setCurrentInterface({ type: 'text', placeholder: "Type your response..." });
    }
    
    // Handle initial document state from API response
    if (data.document) {
      setResumeDocument(data.document);
    }
    
    // Note: State hierarchy is now sent via WebSocket, not HTTP response
    
    // HTTP fallback for message content
    if (data.question && pendingMessageIdRef.current && !hasReceivedFirstTokenRef.current) {
      const messageId = pendingMessageIdRef.current;
      const questionContent = data.question;
      
      setMessages(prevMessages => {
        const existingIndex = prevMessages.findIndex(m => m.id === messageId);
        if (existingIndex >= 0) {
          return prevMessages.map((msg, idx) => 
            idx === existingIndex 
              ? { ...msg, content: questionContent }
              : msg
          );
        } else {
          const newMessage: Message = {
            id: messageId,
            role: 'assistant',
            content: questionContent,
            // No timestamp for assistant messages
            interface: data.interface
          };
          return [...prevMessages, newMessage];
        }
      });
      
      setIsLoading(false);
      hasReceivedFirstTokenRef.current = true;
      pendingMessageIdRef.current = null;
      apiCallInProgressRef.current = false;
    }
  }, []);

  // Helper function to get tool call display message
  const getToolCallMessage = (toolName: string): string => {
    switch (toolName) {
      case 'web_search':
        return 'Searching the web...';
      case 'append_to_resume':
        return 'Adding content to resume...';
      case 'edit_resume_section':
        return 'Updating resume section...';
      case 'add_resume_section':
        return 'Adding new resume section...';
      case 'add_to_existing_section':
        return 'Adding to resume section...';
      case 'create_secure_form':
        return 'Creating secure form...';
      case 'add_todo_item':
        return 'Adding task...';
      case 'complete_todo_item':
        return 'Completing task...';
      case 'list_pending_todos':
        return 'Retrieving task list...';
      default:
        if (toolName.startsWith('transition')) {
          return 'Updating interview stage...';
        }
        return `Working on ${toolName.replace(/_/g, ' ')}...`;
    }
  };

  // Global WebSocket listeners for messages that can arrive before version is set
  useEffect(() => {
    
    // Handler for direct messages (like welcome message from initProject)
    const handleGlobalResumeInterviewResponse = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewResponse' && 
          message.type === 'message' &&
          message.content) {
        
        // Add the message to the chat
        const newMessage: Message = {
          id: uuidv4(),
          role: 'assistant',
          content: message.content,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, newMessage]);
        
        // For welcome messages, we need to:
        // 1. Stop spinning immediately (the message is already complete)
        // 2. Clear loading state
        
        // For a complete welcome message, stop spinning only (no repositioning)
        bowtie.onWelcomeMessage();
        setIsLoading(false);
        
        // If we're in initial flow, this welcome message ends it
        if (isInitialFlow) {
          setIsInitialFlow(false);
        }
      }
    };
    
    // Handler for industry form requests (can arrive immediately after project creation)
    const handleGlobalIndustryFormRequest = (message: WebSocketMessage) => {
      if (message.action === 'requestIndustryInfo' && 
          message.industryFormConfig) {
        
        // Create a new message with the industry form
        const formMessage: Message = {
          id: message.messageId || uuidv4(),
          role: 'assistant',
          content: '',
          industryForm: message.industryFormConfig,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, formMessage]);
        
        // For initial flow, the form IS the AI response, and it's complete
        if (isInitialFlow) {
          // The form is ready for user input
          bowtie.onFormReceived();
          setIsLoading(false);
        } else {
          // For non-initial flow, form is part of ongoing AI response
          bowtie.onFormReceived();
        }
        
        // Autoscroll after a brief delay
        setTimeout(() => {
          autoscroll('smooth');
        }, 100);
      }
    };
    
    // Set up global handlers
    webSocketService.on('resumeInterviewResponse', handleGlobalResumeInterviewResponse);
    webSocketService.on('requestIndustryInfo', handleGlobalIndustryFormRequest);
    
    return () => {
      webSocketService.off('resumeInterviewResponse', handleGlobalResumeInterviewResponse);
      webSocketService.off('requestIndustryInfo', handleGlobalIndustryFormRequest);
    };
  }, [bowtie, isInitialFlow]); // Include bowtie in dependencies

  // WebSocket listeners for version-specific messages
  useEffect(() => {
    const resumeInterviewId = activeVersionId;
    if (!resumeInterviewId) return;

    const handleStreamStart = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewStreamStart' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.streamMessageId) {
        
        // Don't create the message immediately - wait for first token
        // This prevents the assistant message from appearing before tool calls
        currentStreamingMessageIdRef.current = message.streamMessageId;
        hasReceivedFirstTokenRef.current = false;
        
        // Auto-switch to chat tab on mobile when receiving a chat message
        if (isMobile && mobileActiveTab === 'document') {
          setMobileActiveTab('chat');
        }
        
        // If this is an auto_text message, stop loading immediately
        if (message.isAutoText) {
          setIsLoading(false);
          hasReceivedFirstTokenRef.current = true; // Prevent token handler from trying to stop loading again
          // Auto-text is textual content - stop spinning
          bowtie.onAutoText();
        } else {
          // Regular stream - AI hasn't started responding yet, still waiting
        }
        
        // Don't autoscroll yet - wait for actual content
      }
    };

    const handleToolSelection = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewToolSelection' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.toolName) {
        
        // Check if this is a transition tool
        const isTransitionTool = message.toolName.startsWith('transition_to_');
        if (isTransitionTool) {
          // For transition tools, immediately trigger the bowtie repositioning
          bowtie.onTransitionToolSelected();
        }
        
        // Create a tool call message with appropriate content
        const toolMessage: Message = {
          id: message.toolCallId || uuidv4(),
          role: 'tool',
          content: getToolCallMessage(message.toolName),
          // No timestamp for tool messages
          toolName: message.toolName,
          toolCallId: message.toolCallId
        };
        
        setMessages(prev => [...prev, toolMessage]);
        
        // Auto-switch to chat tab on mobile when receiving a tool call
        if (isMobile && mobileActiveTab === 'document') {
          setMobileActiveTab('chat');
        }
        
        // Force immediate autoscroll when tool call occurs
        setTimeout(() => autoscroll('smooth'), 100);
      }
    };

    const handleToolExecution = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewToolExecution' && 
          message.resumeInterviewId === resumeInterviewId) {
        
        // Skip loading state for process_upload tool - state transition will handle it
        const shouldSetLoading = message.toolName !== 'process_upload';
        
        // Skip animation for transition tools - they often have immediate auto_text responses
        const isTransitionTool = message.toolName?.startsWith('transition_to_');
        if (isTransitionTool) {
          return;
        }
        
        // Tell bowtie that tool execution completed - trigger repositioning
        bowtie.onToolExecutionComplete();
        
        // Force autoscroll after tool execution
        setTimeout(() => autoscroll('smooth'), 100);
      }
    };


    const handleStreamToken = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewToken' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.streamMessageId === currentStreamingMessageIdRef.current) {
        
        const token = message.token || '';
        const hasTextualContent = token.trim().length > 0;
        
        // Create the assistant message when we receive the first token
        if (!hasReceivedFirstTokenRef.current) {
          hasReceivedFirstTokenRef.current = true;
          setIsLoading(false); // Stop the bowtie spinning when text starts streaming
          
          // Tell bowtie text has started - stop spinning
          bowtie.onStreamStart();
          
          // Create the assistant message now, so it appears after any tool calls
          const newMessage: Message = {
            id: message.streamMessageId!,
            role: 'assistant',
            content: token,
            interface: undefined,
            timestamp: undefined,
          };
          setMessages(prev => [...prev, newMessage]);
        } else {
          // Update existing message with new token
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.streamMessageId
                ? { 
                    ...msg, 
                    content: msg.content + token
                  }
                : msg
            )
          );
        }
        
        // Debounced autoscroll for streaming tokens
        // This prevents too many scroll calls during rapid token updates
        if (!autoscrollDebounceRef.current) {
          autoscrollDebounceRef.current = setTimeout(() => {
            autoscroll('smooth');
            autoscrollDebounceRef.current = null;
          }, 50); // Reduced from 100ms to 50ms for more responsive scrolling
        }
      }
    };

    const handleStreamEnd = (message: WebSocketMessage) => {
      
      if (message.action === 'resumeInterviewStreamEnd' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.streamMessageId === currentStreamingMessageIdRef.current) {
        
        // Update streaming state tracker
        if (message.streamMessageId) {
          streamingStateTracker.handleStreamEnd(message.streamMessageId);
        }
        
        // Ensure the message exists (it should if we received tokens)
        setMessages(prevMessages => {
          const messageExists = prevMessages.some(msg => msg.id === message.streamMessageId);
          if (!messageExists && message.fullMessage) {
            // Create message if it doesn't exist (fallback)
            const newMessage: Message = {
              id: message.streamMessageId!,
              role: 'assistant',
              content: message.fullMessage as string,
              interface: undefined,
              timestamp: undefined,
            };
            return [...prevMessages, newMessage];
          } else {
            // Update existing message
            return prevMessages.map(msg =>
              msg.id === message.streamMessageId
                ? { 
                    ...msg, 
                    content: message.fullMessage ? message.fullMessage as string : msg.content
                  }
                : msg
            );
          }
        });
        
        // Clear all blocking states
        currentStreamingMessageIdRef.current = null;
        pendingMessageIdRef.current = null;
        setIsLoading(false);
        apiCallInProgressRef.current = false;
        currentClientRequestIdRef.current = null;
        hasReceivedFirstTokenRef.current = false;
        
        // Tell bowtie response has completed
        bowtie.onResponseCompleted();
        
        // Final safeguard: ensure body overflow is not stuck
        document.body.style.overflow = 'unset';
        
        // Clear any pending debounced scroll and force final scroll
        if (autoscrollDebounceRef.current) {
          clearTimeout(autoscrollDebounceRef.current);
          autoscrollDebounceRef.current = null;
        }
        setTimeout(() => autoscroll('smooth'), 100);
      } else if (message.action === 'resumeInterviewStreamEnd') {
        
        // If this is for our interview but streamMessageId doesn't match,
        // still clear loading states as a fallback
        if (message.resumeInterviewId === resumeInterviewId) {
          setIsLoading(false);
          apiCallInProgressRef.current = false;
          pendingMessageIdRef.current = null;
          currentStreamingMessageIdRef.current = null;
          currentClientRequestIdRef.current = null;
          hasReceivedFirstTokenRef.current = false;
          
          // Tell bowtie response has completed
          bowtie.onResponseCompleted();
          
          // Update streaming state tracker
          if (message.streamMessageId) {
            streamingStateTracker.handleStreamEnd(message.streamMessageId);
          }
        }
      }
    };

    const handleDocumentUpdate = (message: WebSocketMessage) => {
      if (message.action === 'resumeDocumentUpdate' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.document) {
        
        setResumeDocument(message.document);
        
        // Auto-open panel if closed and we receive a document update
        if (!isDraftPanelOpen && !isMobile) {
          setIsDraftPanelOpen(true);
        }
        
        // Auto-switch to document tab on mobile
        if (isMobile && mobileActiveTab === 'chat') {
          setMobileActiveTab('document');
        }
      }
    };

    const handleStateHierarchyUpdate = (message: WebSocketMessage) => {
      if (message.action === 'resumeInterviewStateHierarchy' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.stateHierarchy) {
        
        
        setStateHierarchy(message.stateHierarchy);
      }
    };

    const handleSecureFormRequest = (message: WebSocketMessage) => {
      if (message.action === 'secureFormRequest' && 
          message.resumeInterviewId === resumeInterviewId && 
          (message.formConfig || message.form)) {
        
        // Handle both 'formConfig' and 'form' field names for backward compatibility
        const formData = message.formConfig || message.form;
        
        // Create a new message with the secure form
        const formMessage: Message = {
          id: message.messageId || uuidv4(),
          role: 'assistant',
          content: '',
          // No timestamp for assistant messages
          secureForm: formData
        };
        
        setMessages(prev => [...prev, formMessage]);
        
        // Auto-switch to chat tab on mobile when receiving a form
        if (isMobile && mobileActiveTab === 'document') {
          setMobileActiveTab('chat');
        }
        
        // Form received - trigger repositioning
        bowtie.onFormReceived();
        
        // Force autoscroll after form display
        setTimeout(() => autoscroll('smooth'), 100);
      }
    };

    // Duplicate handler - using global one instead
    const handleIndustryFormRequest_DISABLED = (message: WebSocketMessage) => {
      if (message.action === 'requestIndustryInfo' && 
          message.industryFormConfig) {
        
        // Accept if it's for our current version OR if we don't have a version yet (new project)
        if (message.resumeInterviewId && resumeInterviewId && message.resumeInterviewId !== resumeInterviewId) {
          return;
        }
        
        // Create a new message with the industry form
        const formMessage: Message = {
          id: message.messageId || uuidv4(),
          role: 'assistant',
          content: '',
          // No timestamp for assistant messages
          industryForm: message.industryFormConfig
        };
        
        setMessages(prev => [...prev, formMessage]);
        
        // Auto-switch to chat tab on mobile when receiving a form
        if (isMobile && mobileActiveTab === 'document') {
          setMobileActiveTab('chat');
        }
        
        // This handler is disabled - using global one instead
      }
    };

    const handleTodoCreated = (message: WebSocketMessage) => {
      if (message.action === 'todoCreated' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.todo) {
        setTodos(prev => [...prev, message.todo]);
      }
    };

    const handleTodoCompleted = (message: WebSocketMessage) => {
      if (message.action === 'todoCompleted' && 
          message.resumeInterviewId === resumeInterviewId && 
          message.todo) {
        setTodos(prev => prev.map(todo => 
          todo.todo_id === message.todo.todo_id ? message.todo : todo
        ));
      }
    };

    const handleDocumentUploadRequest = (message: WebSocketMessage) => {
      
      if (message.action === 'requestDocumentUpload' && 
          message.resumeInterviewId === resumeInterviewId) {
        
        // Create a new message with the upload form
        const uploadMessage: Message = {
          id: message.messageId || uuidv4(),
          role: 'assistant',
          content: '',
          // No timestamp for assistant messages
          documentUploadForm: {
            resumeInterviewId: resumeInterviewId
          }
        };
        
        
        setMessages(prev => {
          const newMessages = [...prev, uploadMessage];
          return newMessages;
        });
        
        // Auto-switch to chat tab on mobile when receiving a form
        if (isMobile && mobileActiveTab === 'document') {
          setMobileActiveTab('chat');
        }
        
        // Form received - trigger repositioning
        bowtie.onFormReceived();
        
        // Force autoscroll after form display
        setTimeout(() => autoscroll('smooth'), 100);
      }
    };

    // Handle project name generation streaming
    const handleProjectNameStreamStart = (message: WebSocketMessage) => {
      if (message.action === 'projectNameStreamStart' && 
          message.projectId && 
          message.streamMessageId) {
        
        // Set a timeout to prevent indefinite cursor blinking
        const timeoutId = setTimeout(() => {
          setProjects(prev => prev.map(p => 
            p.projectId === message.projectId 
              ? { ...p, nameStreaming: false, tempName: undefined, name: p.tempName || p.name || 'Untitled Resume Project' }
              : p
          ));
        }, 30000); // 30 second timeout
        
        // Store timeout ID for cleanup
        (window as any).__projectNameTimeouts = (window as any).__projectNameTimeouts || {};
        (window as any).__projectNameTimeouts[message.projectId] = timeoutId;
        
        // Initialize streaming state for project name
        setProjects(prev => {
          // Check if project exists in the list
          const projectExists = prev.some(p => p.projectId === message.projectId);
          if (!projectExists) {
            // Trigger a refresh of the projects list
            apiService.listProjects().then(projectsData => {
              setProjects(projectsData.projects.map(p => 
                p.projectId === message.projectId 
                  ? { ...p, nameStreaming: true, tempName: '' }
                  : p
              ));
            }).catch(err => {
              console.error('[App] Error refreshing projects:', err);
            });
            return prev;
          }
          
          return prev.map(p => 
            p.projectId === message.projectId 
              ? { ...p, nameStreaming: true, tempName: '' }
              : p
          );
        });
      }
    };

    const handleProjectNameToken = (message: WebSocketMessage) => {
      if (message.action === 'projectNameToken' && 
          message.projectId && 
          message.token) {
        // Update the project name incrementally
        setProjects(prev => {
          const updated = prev.map(p => 
            p.projectId === message.projectId 
              ? { ...p, tempName: (p.tempName || '') + message.token }
              : p
          );
          return updated;
        });
      }
    };

    const handleProjectNameStreamEnd = (message: WebSocketMessage) => {
      if (message.action === 'projectNameStreamEnd' && 
          message.projectId && 
          message.fullName) {
        
        // Clear timeout if it exists
        if ((window as any).__projectNameTimeouts && (window as any).__projectNameTimeouts[message.projectId]) {
          clearTimeout((window as any).__projectNameTimeouts[message.projectId]);
          delete (window as any).__projectNameTimeouts[message.projectId];
        }
        
        // Finalize the project name update
        setProjects(prev => {
          const updated = prev.map(p => 
            p.projectId === message.projectId 
              ? { ...p, name: message.fullName, nameStreaming: false, tempName: undefined }
              : p
          );
          return updated;
        });
        
        // If this is the active project, trigger a refresh to ensure everything is in sync
        if (message.projectId === activeProjectId) {
          // Use the onRefreshProjects callback if available
          setTimeout(() => {
            apiService.listProjects().then(projectsData => {
              setProjects(projectsData.projects);
            }).catch(err => {
              console.error('[App] Error refreshing projects:', err);
            });
          }, 500);
        }
      }
    };

    const handleProjectNameError = (message: WebSocketMessage) => {
      if (message.action === 'projectNameError' && 
          message.projectId) {
        console.error('[App] Project name generation error:', message.error);
        console.error('[App] For project:', message.projectId);
        
        // Clear timeout if it exists
        if ((window as any).__projectNameTimeouts && (window as any).__projectNameTimeouts[message.projectId]) {
          clearTimeout((window as any).__projectNameTimeouts[message.projectId]);
          delete (window as any).__projectNameTimeouts[message.projectId];
        }
        
        // Reset streaming state on error
        setProjects(prev => prev.map(p => 
          p.projectId === message.projectId 
            ? { ...p, nameStreaming: false, tempName: undefined, name: p.name || 'Untitled Resume Project' }
            : p
        ));
      }
    };

    const handleMarketInsightCreated = (data: WebSocketMessage) => {
      // Increment the insights count to show in the notification badge
      setNewInsightsCount(prev => prev + (data.insightCount || 1));
    };

    // Clean up any existing handlers first
    webSocketService.off('resumeInterviewStreamStart', handleStreamStart);
    webSocketService.off('resumeInterviewToken', handleStreamToken);
    webSocketService.off('resumeInterviewStreamEnd', handleStreamEnd);
    webSocketService.off('resumeInterviewToolExecution', handleToolExecution);
    webSocketService.off('resumeInterviewToolSelection', handleToolSelection);
    webSocketService.off('resumeDocumentUpdate', handleDocumentUpdate);
    webSocketService.off('resumeInterviewStateHierarchy', handleStateHierarchyUpdate);
    webSocketService.off('secureFormRequest', handleSecureFormRequest);
    webSocketService.off('todoCreated', handleTodoCreated);
    webSocketService.off('todoCompleted', handleTodoCompleted);
    webSocketService.off('requestDocumentUpload', handleDocumentUploadRequest);
    webSocketService.off('projectNameStreamStart', handleProjectNameStreamStart);
    webSocketService.off('projectNameToken', handleProjectNameToken);
    webSocketService.off('projectNameStreamEnd', handleProjectNameStreamEnd);
    webSocketService.off('projectNameError', handleProjectNameError);
    webSocketService.off('marketInsightCreated', handleMarketInsightCreated);

    // Add new handlers
    webSocketService.on('resumeInterviewStreamStart', handleStreamStart);
    webSocketService.on('resumeInterviewToken', handleStreamToken);
    webSocketService.on('resumeInterviewStreamEnd', handleStreamEnd);
    webSocketService.on('resumeInterviewToolExecution', handleToolExecution);
    webSocketService.on('resumeInterviewToolSelection', handleToolSelection);
    webSocketService.on('resumeDocumentUpdate', handleDocumentUpdate);
    webSocketService.on('resumeInterviewStateHierarchy', handleStateHierarchyUpdate);
    webSocketService.on('secureFormRequest', handleSecureFormRequest);
    webSocketService.on('todoCreated', handleTodoCreated);
    webSocketService.on('todoCompleted', handleTodoCompleted);
    webSocketService.on('requestDocumentUpload', handleDocumentUploadRequest);
    webSocketService.on('projectNameStreamStart', handleProjectNameStreamStart);
    webSocketService.on('projectNameToken', handleProjectNameToken);
    webSocketService.on('projectNameStreamEnd', handleProjectNameStreamEnd);
    webSocketService.on('projectNameError', handleProjectNameError);
    webSocketService.on('marketInsightCreated', handleMarketInsightCreated);

    return () => {
      webSocketService.off('resumeInterviewStreamStart', handleStreamStart);
      webSocketService.off('resumeInterviewToken', handleStreamToken);
      webSocketService.off('resumeInterviewStreamEnd', handleStreamEnd);
      webSocketService.off('resumeInterviewToolExecution', handleToolExecution);
      webSocketService.off('resumeInterviewToolSelection', handleToolSelection);
      webSocketService.off('resumeDocumentUpdate', handleDocumentUpdate);
      webSocketService.off('resumeInterviewStateHierarchy', handleStateHierarchyUpdate);
      webSocketService.off('secureFormRequest', handleSecureFormRequest);
      webSocketService.off('todoCreated', handleTodoCreated);
      webSocketService.off('todoCompleted', handleTodoCompleted);
      webSocketService.off('requestDocumentUpload', handleDocumentUploadRequest);
      webSocketService.off('projectNameStreamStart', handleProjectNameStreamStart);
      webSocketService.off('projectNameToken', handleProjectNameToken);
      webSocketService.off('projectNameStreamEnd', handleProjectNameStreamEnd);
      webSocketService.off('projectNameError', handleProjectNameError);
      webSocketService.off('marketInsightCreated', handleMarketInsightCreated);
    };
  }, [isMobile, mobileActiveTab, isDraftPanelOpen, processApiResponse, stateHierarchy, activeVersionId, activeProjectId, bowtie, isLoading]); // Include necessary dependencies

  // Periodic cleanup of streaming state tracker
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      streamingStateTracker.cleanup();
    }, 60000); // Clean up every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  // Handle addressing insights with AI
  const handleAddressInsights = async () => {
    
    // Close the notifications sidebar
    setIsNotificationsSidebarOpen(false);
    
    // Send a message to the agent to transition to reviewing_insights state
    if (activeVersionId && activeProjectId) {
      const response: UserResponse = {
        type: 'text',
        text: 'I want to review and address the market insights from my job search'
      };
      
      await handleSendResponse(response);
    }
  };

  // Handle new project creation
  const handleNewProject = async () => {
    try {
      // Clear messages from any previous project before creating new one
      setMessages([]);
      
      // Set hasStarted immediately so bowtie appears right away
      setHasStarted(true);
      setIsInitialFlow(true); // Key change: explicitly set initial flow
      
      // Tell bowtie user action started
      bowtie.onUserAction();
      
      // For new project, keep bowtie at top initially
      // Position will update when welcome message arrives
      
      // Call the new initProject endpoint
      const initResponse = await apiService.initProject({ name: 'New Project' });
      
      
      // Extract project and version info from response
      const newProject = {
        projectId: initResponse.projectId,
        name: initResponse.name || 'New Project',
        createdAt: initResponse.createdAt || new Date().toISOString()
      };
      
      // Update projects list
      setProjects(prev => [newProject, ...prev]);
      
      // Switch to the new project/version
      setActiveProjectId(initResponse.projectId);
      setActiveVersionId(initResponse.initialResumeVersionId);
      
      // Clear current state for the new project (except messages which come from WebSocket)
      // Don't clear messages - they will be populated by WebSocket handlers
      setResumeDocument('');
      setStateHierarchy(null);
      setTodos([]);
      
      
      // The backend will send the welcome message and industry form via WebSocket
      // No need to send "Hello" message anymore
    } catch (err) {
      console.error('[App] Error creating new project:', err);
      setError(err instanceof Error ? err.message : 'Failed to create new project');
      setIsInitialFlow(false); // Reset on error
      // Reset bowtie state on error by stopping spinning
      bowtie.onResponseCompleted();
    }
  };
  
  // Handle project/version selection
  const handleProjectSelect = async (projectId: string, versionId: string) => {
    
    if (projectId === activeProjectId && versionId === activeVersionId) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Load the full version state
      const versionData = await apiService.getResumeVersion(versionId);
      
      // Update active project/version
      setActiveProjectId(projectId);
      setActiveVersionId(versionId);
      
      // Clear current state
      setMessages([]);
      setResumeDocument('');
      setStateHierarchy(null);
      setTodos([]);
      
      // Restore chat history
      if (versionData.chatHistory && versionData.chatHistory.length > 0) {
        const restoredMessages = versionData.chatHistory
          .filter((msg: any, index: number) => {
            // Skip the first message if it's a user message with content "Hello"
            if (index === 0 && msg.type === 'human' && msg.content === 'Hello') {
              return false;
            }
            return true;
          })
          .map((msg: any) => ({
            id: uuidv4(),
            role: msg.type === 'human' ? 'user' : msg.type === 'ai' ? 'assistant' : 'tool',
            content: msg.content,
            timestamp: new Date(),
            toolName: msg.tool_name,
            toolCallId: msg.tool_call_id
          }));
        setMessages(restoredMessages);
      }
      
      // Restore resume document
      if (versionData.resumeDocument?.content) {
        setResumeDocument(versionData.resumeDocument.content);
      }
      
      // Restore state hierarchy
      if (versionData.stateData?.currentStateHierarchy) {
        setStateHierarchy(versionData.stateData);
      }
      
      // Restore todos
      if (versionData.todos && versionData.todos.length > 0) {
        setTodos(versionData.todos);
      }
      
      setHasStarted(true);
      setIsLoading(false);
      
    } catch (err) {
      console.error('[App] Error loading version:', err);
      setError(err instanceof Error ? err.message : 'Failed to load version');
      setIsLoading(false);
    }
  };
  
  // Handle branch switching from BranchNavigator
  const handleBranchSwitch = async (versionId: string) => {
    
    // Find the project ID for this version
    let targetProjectId = activeProjectId;
    
    // If we need to find the project ID, we could search through projects
    // For now, we'll assume it's in the same project
    if (!targetProjectId) {
      console.error('[App] No active project ID for branch switch');
      return;
    }
    
    // Use the existing handleProjectSelect function
    await handleProjectSelect(targetProjectId, versionId);
  };
  
  // Load projects and resume versions when authenticated
  const loadProjectsAndVersions = React.useCallback(async () => {
    // Triple-check to prevent duplicate calls
    if (initialInterviewStartedRef.current) {
      return;
    }
    
    if (apiCallInProgressRef.current) {
      return;
    }

    // Immediately set the flag to prevent any other calls
    initialInterviewStartedRef.current = true;
    
    apiCallInProgressRef.current = true;
    setIsLoading(true);

    try {
      // Step 1: Get user's projects
      const projectsData = await apiService.listProjects();
      setProjects(projectsData.projects);
      
      if (projectsData.projects.length === 0) {
        // No projects exist - create a new one
        const newProject = await apiService.createProject({ name: 'New Project' });
        
        // Use the initialResumeVersionId if returned from the API
        const versionId = newProject.initialResumeVersionId;
        if (versionId) {
          setActiveProjectId(newProject.projectId);
          setActiveVersionId(versionId);
        } else {
          // Fallback: create a new version if not returned
          const newVersion = await apiService.createResumeVersion(newProject.projectId, { name: 'Version 1' });
          setActiveProjectId(newProject.projectId);
          setActiveVersionId(newVersion.resumeVersionId);
        }
        
        setProjects([newProject]);
        
        // Start conversation with the new version
        setHasStarted(true);
        setIsLoading(false);
        apiCallInProgressRef.current = false;
        
        // Don't automatically send a message - wait for user to interact with the form
      } else {
        // Projects exist - load the most recent one
        const mostRecentProject = projectsData.projects[0]; // Already sorted by updatedAt
        
        // Get project details including versions
        const projectDetails = await apiService.getProjectDetails(mostRecentProject.projectId);
        
        if (projectDetails.resumeVersions && projectDetails.resumeVersions.length > 0) {
          // Use the most recent version
          const mostRecentVersion = projectDetails.resumeVersions[0];
          setActiveProjectId(mostRecentProject.projectId);
          setActiveVersionId(mostRecentVersion.resumeVersionId);
          
          // Load the full version state
          const versionData = await apiService.getResumeVersion(mostRecentVersion.resumeVersionId);
          
          // Restore chat history
          if (versionData.chatHistory && versionData.chatHistory.length > 0) {
            const restoredMessages = versionData.chatHistory
              .filter((msg: any, index: number) => {
                // Skip the first message if it's a user message with content "Hello"
                if (index === 0 && msg.type === 'human' && msg.content === 'Hello') {
                  return false;
                }
                return true;
              })
              .map((msg: any) => ({
                id: uuidv4(),
                role: msg.type === 'human' ? 'user' : msg.type === 'ai' ? 'assistant' : 'tool',
                content: msg.content,
                timestamp: new Date(),
                toolName: msg.tool_name,
                toolCallId: msg.tool_call_id
              }));
            setMessages(restoredMessages);
          }
          
          // Restore resume document
          if (versionData.resumeDocument?.content) {
            setResumeDocument(versionData.resumeDocument.content);
          }
          
          // Restore state hierarchy
          if (versionData.stateData?.currentStateHierarchy) {
            setStateHierarchy(versionData.stateData);
          }
          
          // Restore todos
          if (versionData.todos && versionData.todos.length > 0) {
            setTodos(versionData.todos);
          }
          
          setHasStarted(true);
          setIsLoading(false);
          apiCallInProgressRef.current = false;
          
          // After loading an existing version, check if its chat history is empty
          if (!versionData.chatHistory || versionData.chatHistory.length === 0) {
            // Don't automatically send a message - wait for user to interact
          }
        } else {
          // Project has no versions - create one
          const newVersion = await apiService.createResumeVersion(mostRecentProject.projectId, { name: 'Version 1' });
          
          setActiveProjectId(mostRecentProject.projectId);
          setActiveVersionId(newVersion.resumeVersionId);
          
          setHasStarted(true);
          setIsLoading(false);
          apiCallInProgressRef.current = false;
          
          // After creating a new version for existing project, wait for user input
        }
      }
    } catch (err) {
      console.error('[App] Error loading projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      setIsLoading(false);
      apiCallInProgressRef.current = false;
    }
  }, [processApiResponse]);

  // Handle sending responses
  const handleSendResponse = async (response: UserResponse | any, newStateData?: any) => {
    // ADD THIS LOG:
    console.log(
      '%c[SEND_RESPONSE] handleSendResponse called. newStateData is:',
      'color: #ffc107; font-weight: bold;',
      newStateData || 'Not Present'
    );
    
    // Handle fork response type for conversation branching
    if (response.type === 'fork' && response.newVersionId && response.editedContent) {
      
      // Switch to the new version
      setActiveVersionId(response.newVersionId);
      
      // Clear current conversation
      setMessages([]);
      setResumeDocument('');
      setTodos([]);
      
      // Load the forked version data
      try {
        const versionData = await apiService.getResumeVersion(response.newVersionId);
        
        // Restore chat history from the fork
        if (versionData.chatHistory && versionData.chatHistory.length > 0) {
          const restoredMessages = versionData.chatHistory.map((msg: any) => ({
            id: uuidv4(),
            role: msg.type === 'human' ? 'user' : msg.type === 'ai' ? 'assistant' : 'tool',
            content: msg.content,
            timestamp: new Date(),
            toolName: msg.tool_name,
            toolCallId: msg.tool_call_id
          }));
          setMessages(restoredMessages);
        }
        
        // Restore resume document
        if (versionData.resumeDocument?.content) {
          setResumeDocument(versionData.resumeDocument.content);
        }
        
        // Now send the edited content as a new message
        const editedResponse: UserResponse = {
          type: 'text',
          text: response.editedContent
        };
        
        // Use setTimeout to ensure state updates have completed
        setTimeout(() => {
          handleSendResponse(editedResponse);
        }, 100);
        
      } catch (err) {
        console.error('[App] Error loading forked version:', err);
        setError(err instanceof Error ? err.message : 'Failed to load forked version');
      }
      
      return;
    }
    
    // Special case: Always allow [Uploaded Document] message to go through
    const isUploadedDocumentMessage = response.type === 'text' && response.text === '[Uploaded Document]';
    
    if (!isUploadedDocumentMessage && (isLoading || pendingApiCallDetails || apiCallInProgressRef.current)) {
      return;
    }
    
    if (isUploadedDocumentMessage) {
    }

    const responseText = response.type === 'text' ? response.text : response.label;
    if (!responseText?.trim()) return;

    setCurrentInput('');
    setError(null);
    
    // Clear streaming state
    currentStreamingMessageIdRef.current = null;
    hasReceivedFirstTokenRef.current = false;
    pendingMessageIdRef.current = null;
    
    // Check if this is the initial "Hello" message that should be hidden
    const isInitialHelloMessage = response.type === 'text' && response.text === 'Hello';
    
    // Add user message (but skip if it's the initial Hello)
    const messageId = uuidv4();
    if (!isInitialHelloMessage) {
      const message: Message = { 
        id: messageId, 
        role: 'user', 
        content: responseText, 
        timestamp: new Date() 
      };
      setMessages(prev => [...prev, message]);
      
      // Force immediate autoscroll when user sends message
      setTimeout(() => autoscroll('smooth'), 50);
    }
    
    // Trigger animation and API call (even for Hello message)
    setPendingApiCallDetails({ response, messageId, newStateData });
  };

  // Handle secure form submission
  const handleSecureFormSubmit = async (formId: string, formData: Record<string, any>) => {
    if (isLoading || apiCallInProgressRef.current) {
      return;
    }

    setError(null);
    
    // Tell bowtie user sent a message (form submission)
    bowtie.onUserAction();
    
    hasReceivedFirstTokenRef.current = false;
    setIsLoading(true);
    
    apiCallInProgressRef.current = true;

    try {
      
      const { data: result, clientRequestId } = await apiService.submitSecureForm(
        activeVersionId || '', 
        activeProjectId || '',
        formId, 
        formData
      );
      currentClientRequestIdRef.current = clientRequestId;
      
      
      // The backend will send updated messages via WebSocket
      // so we don't need to update the UI here
    } catch (err) {
      console.error('[App] Error submitting secure form:', err);
      if (err instanceof Error) {
      }
      // Check if it's a network timeout (gateway timeout)
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        // Don't clear loading state - wait for WebSocket stream to end
      } else {
        setError(err instanceof Error ? err.message : 'Failed to submit secure form');
        setIsLoading(false);
        apiCallInProgressRef.current = false;
        // Focus input field after form submission error
        setTimeout(() => {
          if (inputRef.current && !inputRef.current.disabled) {
            inputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  // Handle industry form submission
  const handleIndustryFormSubmit = async (formData: { industryGroup: string; yearsOfExperience: number }) => {
    if (isLoading || apiCallInProgressRef.current) {
      return;
    }

    setError(null);
    
    // Tell bowtie user sent a message (form submission)
    bowtie.onUserAction();
    
    hasReceivedFirstTokenRef.current = false;
    setIsLoading(true);
    
    apiCallInProgressRef.current = true;

    try {
      
      const { data: result, clientRequestId } = await apiService.submitIndustryInfo(
        activeVersionId || '', 
        activeProjectId || '',
        formData.industryGroup, 
        formData.yearsOfExperience
      );
      currentClientRequestIdRef.current = clientRequestId;
      
      
      // The backend will send updated messages via WebSocket
      // so we don't need to update the UI here
    } catch (err) {
      console.error('[App] Error submitting industry form:', err);
      if (err instanceof Error) {
      }
      // Check if it's a network timeout (gateway timeout)
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        // Don't clear loading state - wait for WebSocket stream to end
      } else {
        setError(err instanceof Error ? err.message : 'Failed to submit industry form');
        setIsLoading(false);
        apiCallInProgressRef.current = false;
        // Focus input field after form submission error
        setTimeout(() => {
          if (inputRef.current && !inputRef.current.disabled) {
            inputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  // Handle upload start
  const handleUploadStart = () => {
    setUploadModalOpen(true);
  };
  
  // Handle upload stage change
  const handleUploadStageChange = (stage: UploadStage | null) => {
    setUploadStage(stage);
  };

  // Handle upload complete
  const handleUploadComplete = async (message: WebSocketMessage) => {
    // ADD THIS LOG:
    console.log(
      '%c[UPLOAD_COMPLETE] Received newStateData via WebSocket:',
      'color: #28a745; font-weight: bold;',
      message.newStateData || 'Not Present'
    );

    // FIX: Apply new state directly from upload completion message
    if (message.newStateData) {
      setStateHierarchy(message.newStateData);
    }

    // Send a special message to the agent to trigger the next conversational turn.
    // This confirms the upload is done and asks the agent what's next.
    const newStateData = message.newStateData;
    await handleSendResponse({ type: 'text', text: '[Uploaded Document]' }, newStateData);

    // Ensure modal closes even if the automatic close fails
    // The modal should handle this itself, but we add a fallback
    setTimeout(() => {
      if (uploadModalOpen) {
        handleUploadModalClose();
      }
    }, 3000); // 3 second fallback
  };
  
  // Handle upload modal close
  const handleUploadModalClose = () => {
    setUploadModalOpen(false);
    setUploadStage(null); // Reset immediately since we removed animations
    // Ensure body overflow is reset as a final safeguard
    document.body.style.overflow = 'unset';
  };

  // Handle upload error
  const handleUploadError = (error: string) => {
    console.error('[App] Resume upload error:', error);
    setError(`Upload failed: ${error}`);
    setUploadModalOpen(false);
    setUploadStage(null);
  };

  // Handle animation and API call
  useEffect(() => {
    if (pendingApiCallDetails && chatAreaRef.current && !apiCallInProgressRef.current) {
      const { response, messageId, newStateData } = pendingApiCallDetails;
      
      apiCallInProgressRef.current = true;
      
      // Tell bowtie user sent a message
      bowtie.onUserAction();
      
      hasReceivedFirstTokenRef.current = false;
      setPendingApiCallDetails(null);

      (async () => {
        try {
          setIsLoading(true); // This will trigger the new useEffect for scrolling if needed
          
          const expectedMessageId = uuidv4();
          pendingMessageIdRef.current = expectedMessageId;
          
          const { data, clientRequestId } = await apiService.sendResumeInterviewResponse(activeVersionId || '', activeProjectId || '', response, newStateData);
          currentClientRequestIdRef.current = clientRequestId;
          
          setTimeout(() => {
            if (pendingMessageIdRef.current === expectedMessageId && !hasReceivedFirstTokenRef.current) {
              if (data && Object.keys(data).length > 0) {
                processApiResponse(data);
              }
            }
          }, 2000);
        } catch (err) {
          console.error('[App] Error sending response:', err);
          
          if (err instanceof Error) {
          }
          // Check if it's a network timeout (gateway timeout)
          if (err instanceof TypeError && err.message === 'Failed to fetch') {
            // Don't clear loading state - wait for WebSocket stream to end
          } else {
            setError(err instanceof Error ? err.message : 'Failed to send response');
            setIsLoading(false);
            pendingMessageIdRef.current = null;
            apiCallInProgressRef.current = false;
          }
        }
      })();
    }
  }, [pendingApiCallDetails, processApiResponse, activeVersionId, activeProjectId]);




  // Single unified autoscroll effect
  useEffect(() => {
    // Always scroll when messages change, loading state changes, or bowtie moves
    const scrollTimer = setTimeout(() => {
      autoscroll('smooth');
    }, 150); // Balanced delay for DOM updates

    return () => clearTimeout(scrollTimer);
  }, [messages.length, messages[messages.length - 1]?.content, isLoading, bowtie.bowtiePosition]);

  // Start interview when authenticated - with debounce to prevent multiple calls
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Add a delay to ensure WebSocket is connected before starting interview
    const timeoutId = setTimeout(() => {
      if (isAuthenticated && !initialInterviewStartedRef.current) {
        // Check if WebSocket is connected
        if (!webSocketService.isConnected()) {
          // Try again in a bit
          const retryTimeout = setTimeout(() => {
            if (isAuthenticated && !initialInterviewStartedRef.current && webSocketService.isConnected()) {
              loadProjectsAndVersions();
            }
          }, 500);
          return () => clearTimeout(retryTimeout);
        } else {
          loadProjectsAndVersions();
        }
      }
    }, 500); // 500ms delay to ensure WebSocket has time to connect

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, loadProjectsAndVersions]);

  // Focus management - automatically focus input for continuous typing
  useEffect(() => {
    if (messages.length === 0 || isLoading) return;
    
    const lastMessage = messages[messages.length - 1];
    
    // If the most recent message contains a secure form, don't focus the main input
    // (secure form handles its own focus)
    if (lastMessage.secureForm) {
      return;
    }
    
    // Otherwise, always focus the main input for continuous typing
    setTimeout(() => {
      if (inputRef.current && !inputRef.current.disabled) {
        inputRef.current.focus();
      }
    }, 100);
  }, [messages, isLoading]);

  // Theme functions
  const toggleTheme = () => {
    setTheme(current => {
      if (current === 'light') return 'dark';
      if (current === 'dark') return 'auto';
      return 'light';
    });
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5"/>
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
        );
      case 'dark':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        );
      case 'auto':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        );
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light': return 'Light';
      case 'dark': return 'Dark';
      case 'auto': return 'Auto';
    }
  };

  // Render message
  const renderMessage = (message: Message, messageIndex: number) => {
    const isLastMessage = messageIndex === messages.length - 1;
    const shouldShowTimestamp = !!message.timestamp;

    return (
      <div 
        key={message.id} 
        className={`${interviewStyles.message} ${interviewStyles[message.role]}`}
        ref={isLastMessage ? lastMessageRef : null}
      >
        <div className={interviewStyles.messageContent}>
          {message.role === 'assistant' ? (
            <>
              {message.content && (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
              {message.secureForm && (
                <SecureForm
                  formId={message.secureForm.formId || message.secureForm.form_id || ''}
                  title={message.secureForm.title}
                  description={message.secureForm.description}
                  fields={message.secureForm.fields}
                  onSubmit={handleSecureFormSubmit}
                />
              )}
              {message.industryForm && (
                <IndustrySelectionForm
                  formId={message.industryForm.formId}
                  title={message.industryForm.title}
                  description={message.industryForm.description}
                  industryGroups={message.industryForm.industryGroups}
                  experienceOptions={message.industryForm.experienceOptions}
                  onSubmit={handleIndustryFormSubmit}
                />
              )}
            </>
          ) : message.role === 'tool' ? (
            <div className={interviewStyles.toolMessage}>
              {message.content}
            </div>
          ) : (
            <p>{message.content}</p>
          )}
          {shouldShowTimestamp && message.timestamp && (
            <span className={interviewStyles.timestamp}>
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Handle form submit
  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentInput.trim()) {
      handleSendResponse({ type: 'text', text: currentInput.trim() });
    }
  };

  // Handle mobile tab switching
  const handleMobileTabChange = (tab: 'chat' | 'document') => {
    setMobileActiveTab(tab);
  };

  // Handle draft panel toggle
  const handleDraftPanelToggle = () => {
    setIsDraftPanelOpen(!isDraftPanelOpen);
  };

  // Handle draft panel resize
  const handleDraftPanelResize = (width: number) => {
    setDraftPanelWidth(width);
  };

  // Handle draft panel wide toggle
  const handleDraftPanelToggleWide = () => {
    if (isDraftPanelOpenWide) {
      // Closing wide mode - restore previous width
      setDraftPanelWidth(draftPanelPreviousWidth);
      setIsDraftPanelOpenWide(false);
    } else {
      // Opening wide mode - store current width
      setDraftPanelPreviousWidth(draftPanelWidth);
      setIsDraftPanelOpenWide(true);
    }
  };

  // Public layout
  const renderPublicLayout = () => (
    <div className={styles.app}>
      <Header
        isAuthenticated={false}
        user={null}
        theme={theme}
        toggleTheme={toggleTheme}
        onSignInClick={() => {
          setAuthDefaultTab('signIn');
          setShowAuthModal(true);
        }}
        onJoinClick={() => {
          setAuthDefaultTab('signUp');
          setShowAuthModal(true);
        }}
        getThemeIcon={getThemeIcon}
        getThemeLabel={getThemeLabel}
      />

      <HeroSection 
        boldText="Command your career narrative."
        mainText="Create and customize the resume you envision by having a smart, guided conversation."
      />

      <main className={styles.main}>
        <div className={styles.mainContainer}>
          <div className={styles.marketingContent}>
            <div className={styles.marketingSection}>
              <div className={styles.featuresGrid}>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9 21h6M12 3a6 6 0 0 1 6 6c0 2.5-1.5 4.5-3 5.5V17a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-2.5C7.5 13.5 6 11.5 6 9a6 6 0 0 1 6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 9h4M10.5 11h3M11 13h2" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
                    </svg>
                  </div>
                  <h3 className={styles.featureTitle}>Intelligent guidance</h3>
                  <p className={styles.featureDescription}>
                    Craft your resume with confidence and ease. Our guided process reduces mental load and puts you in control of every streamlined decision.
                  </p>
                </div>
                
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className={styles.featureTitle}>Enhance or Create</h3>
                  <p className={styles.featureDescription}>
                    Upload your old resume or start fresh. Our smart tools make it easy to put your best foot forward.
                  </p>
                </div>
                
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className={styles.featureTitle}>Intuitive refinement</h3>
                  <p className={styles.featureDescription}>
                    Tweak and polish to perfection, or delegate to AI. Handsome CV lets you put your focus where it matters most.
                  </p>
                </div>
              </div>
              
              <div className={styles.ctaSection}>
                <h3 className={styles.ctaTitle}>Ready to get started?</h3>
                <p className={styles.ctaDescription}>
                  Take the first step toward an attractive resume.
                </p>
                <button onClick={() => {
                  setAuthDefaultTab('signUp');
                  setShowAuthModal(true);
                }} className={styles.ctaButton}>
                  Sign in to try it free
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );

  // Authenticated layout with ResumeDraft integration
  const renderAuthenticatedLayout = (signOut: () => void, user: any) => {
    // Mobile view with tabs
    if (isMobile) {
      return (
        <div className={`${styles.app} ${styles.authenticatedLayout}`}>
          <Header
            isAuthenticated={true}
            user={user}
            signOut={signOut}
            theme={theme}
            toggleTheme={toggleTheme}
            onSignInClick={() => {}}
            onJoinClick={() => {}}
            getThemeIcon={getThemeIcon}
            getThemeLabel={getThemeLabel}
            onMenuClick={() => {
              if (isSidebarToggleOpen) {
                setIsSidebarToggleOpen(false);
                setIsSidebarHoverOpen(false);
              } else {
                setIsSidebarToggleOpen(true);
              }
            }}
            onNewProjectClick={handleNewProject}
            isMenuOpen={isSidebarToggleOpen}
            onMenuHoverEnter={() => {
              if (!isSidebarToggleOpen) {
                setIsSidebarHoverOpen(true);
              }
            }}
            onMenuHoverLeave={() => {
              // Don't close sidebar here - hover zone will handle it
            }}
            newInsightsCount={newInsightsCount}
            onNotificationsClick={() => setIsNotificationsSidebarOpen(true)}
          />

          <MobileTabView
            activeTab={mobileActiveTab}
            onTabChange={handleMobileTabChange}
            document={resumeDocument}
          >
            {/* Chat content for mobile */}
            <div className={interviewStyles.resumeInterview}>
              <div className={interviewStyles.chatAreaWrapper} ref={chatAreaRef}>
                {hasStarted && (
                  <div className={interviewStyles.singleBowtieContainer} style={{ top: `${bowtie.bowtiePosition}px` }}>
                    <BowtieIcon 
                      width={36} 
                      height={36} 
                      className={bowtie.isSpinning ? interviewStyles.loadingBowtie : ''} 
                    />
                  </div>
                )}
                {messages.map((msg, msgIdx) => {
                  return renderMessage(msg, msgIdx);
                })}
                <div ref={autoScrollSpacerRef} className={interviewStyles.autoScrollSpacer} />
              </div>
              
              <div 
                className={interviewStyles.inputArea}
                style={{ 
                  right: isDraftPanelOpen ? `${draftPanelWidth}px` : '0'
                }}
              >
                {error && (
                  <div className={interviewStyles.errorMessage}>
                    {error}
                  </div>
                )}
                <div className={interviewStyles.inputAreaContent}>
                  <form onSubmit={handleTextSubmit} className={interviewStyles.inputForm}>
                    <div className={interviewStyles.inputContainer}>
                      <input
                        ref={inputRef}
                        type="text"
                        value={currentInput}
                        onChange={(e) => setCurrentInput(e.target.value)}
                        placeholder={currentInterface.placeholder || "Type your response..."}
                        className={interviewStyles.textInput}
                        disabled={isLoading || apiCallInProgressRef.current}
                      />
                      <button
                        type="submit"
                        disabled={!currentInput.trim() || isLoading || apiCallInProgressRef.current}
                        className={interviewStyles.sendButton}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="19" x2="12" y2="5"/>
                          <polyline points="5 12 12 5 19 12"/>
                        </svg>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </MobileTabView>
          
          {/* Notifications Sidebar - Mobile */}
          <NotificationsSidebar
            isOpen={isNotificationsSidebarOpen}
            onClose={() => {
              setIsNotificationsSidebarOpen(false);
              setNewInsightsCount(0); // Reset count when sidebar is opened
            }}
            projectId={activeProjectId || ''}
            versionId={activeVersionId || ''}
            onAddressInsights={handleAddressInsights}
          />

        </div>
      );
    }

    // Desktop view with side panel
    return (
      <div className={`${styles.app} ${styles.authenticatedLayout}`}>
        <Header
          isAuthenticated={true}
          user={user}
          signOut={signOut}
          theme={theme}
          toggleTheme={toggleTheme}
          onSignInClick={() => {}}
          onJoinClick={() => {}}
          getThemeIcon={getThemeIcon}
          getThemeLabel={getThemeLabel}
          onMenuClick={() => {
            if (isSidebarToggleOpen) {
              setIsSidebarToggleOpen(false);
              setIsSidebarHoverOpen(false);
            } else {
              setIsSidebarToggleOpen(true);
            }
          }}
          onNewProjectClick={handleNewProject}
          isMenuOpen={isSidebarToggleOpen}
          onMenuHoverEnter={() => {
            if (!isSidebarToggleOpen) {
              setIsSidebarHoverOpen(true);
            }
          }}
          onMenuHoverLeave={() => {
            // Don't close sidebar here - the hover zone will handle it
          }}
          newInsightsCount={newInsightsCount}
          onNotificationsClick={() => setIsNotificationsSidebarOpen(true)}
        />
        
        {/* Hover zone from header to sidebar to maintain open state */}
        {(isSidebarHoverOpen || isSidebarToggleOpen) && (
          <div
            onMouseEnter={() => {
              if (!isSidebarToggleOpen) {
                setIsSidebarHoverOpen(true);
              }
            }}
            onMouseLeave={() => {
              if (!isSidebarToggleOpen) {
                setIsSidebarHoverOpen(false);
              }
            }}
            style={{ 
              position: 'fixed', 
              left: 0, 
              top: 0, 
              width: '350px', // Wider to cover sidebar + gap
              height: '100vh', 
              zIndex: 997, // Below sidebar
              pointerEvents: 'auto'
            }}
          />
        )}
        <ProjectsSidebar
          projects={projects}
          activeProjectId={activeProjectId}
          activeVersionId={activeVersionId}
          onProjectSelect={handleProjectSelect}
          onNewProject={handleNewProject}
          onRefreshProjects={loadProjectsAndVersions}
          isExpanded={isSidebarHoverOpen || isSidebarToggleOpen}
        />

        <main className={styles.main}>
          <ErrorDisplay error={error} onDismiss={() => setError(null)} />
          
          {/* Use the new ResumeInterview component */}
          <ResumeInterview
            messages={messages}
            currentInput={currentInput}
            setCurrentInput={setCurrentInput}
            isLoading={isLoading}
            hasStarted={hasStarted}
            currentInterface={currentInterface}
            resumeDocument={resumeDocument}
            isDraftPanelOpen={isDraftPanelOpen}
            draftPanelWidth={draftPanelWidth}
            isDraftPanelOpenWide={isDraftPanelOpenWide}
            bowtieTop={bowtie.bowtiePosition}
            bowtieIsSpinning={bowtie.isSpinning}
            isAnimatingDownward={false}
            apiCallInProgressRef={apiCallInProgressRef}
            chatAreaRef={chatAreaRef}
            lastMessageRef={lastMessageRef}
            autoScrollSpacerRef={autoScrollSpacerRef}
            inputRef={inputRef}
            error={error}
            stateHierarchy={stateHierarchy}
            todos={todos}
            currentStreamingMessageId={currentStreamingMessageIdRef.current}
            onSendResponse={handleSendResponse}
            onDraftPanelToggle={handleDraftPanelToggle}
            onDraftPanelResize={handleDraftPanelResize}
            onDraftPanelToggleWide={handleDraftPanelToggleWide}
            onSecureFormSubmit={handleSecureFormSubmit}
            onIndustryFormSubmit={handleIndustryFormSubmit}
            onUploadComplete={handleUploadComplete}
            onUploadError={handleUploadError}
            onUploadStart={handleUploadStart}
            onUploadStageChange={handleUploadStageChange}
            activeVersionId={activeVersionId}
            onHistoryRestore={loadProjectsAndVersions}
            onBranchSwitch={handleBranchSwitch}
          />
        </main>
        
        {/* Upload Modal - rendered outside main content to blur everything */}
        <UploadModal
          isOpen={uploadModalOpen}
          currentStage={uploadStage}
          onClose={handleUploadModalClose}
        />
        
        {/* Notifications Sidebar */}
        <NotificationsSidebar
          isOpen={isNotificationsSidebarOpen}
          onClose={() => {
            setIsNotificationsSidebarOpen(false);
            setNewInsightsCount(0); // Reset count when sidebar is opened
          }}
          projectId={activeProjectId || ''}
          versionId={activeVersionId || ''}
          onAddressInsights={handleAddressInsights}
        />
      </div>
    );
  };

  // Track authentication status from CustomAuthenticator
  const [authInfo, setAuthInfo] = useState<{ signOut?: () => void; user?: any; isAuthenticated: boolean }>({ isAuthenticated: false });

  return (
    <CustomAuthenticator 
      showModal={showAuthModal}
      onModalClose={() => setShowAuthModal(false)}
      defaultTab={authDefaultTab}
    >
      {({ signOut, user, isAuthenticated: authStatus }) => {
        // Store auth info in state to use in effects
        if (authInfo.isAuthenticated !== authStatus) {
          setAuthInfo({ signOut, user, isAuthenticated: authStatus });
          setIsAuthenticated(authStatus);
        }

        if (authStatus && user) {
          return renderAuthenticatedLayout(signOut!, user);
        }
        return renderPublicLayout();
      }}
    </CustomAuthenticator>
  );
}

export default App;